import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiDownload, FiShoppingBag, FiBookOpen } from 'react-icons/fi';
import api from '../../services/api';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    max-width: 600px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
    max-width: 100%;
  }
`;


const ConfirmationCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
    margin-bottom: 0.5rem;
    border-left: none;
    border-right: none;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem auto;
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    margin: 0 auto 1.5rem auto;
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    margin: 0 auto 1rem auto;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0 0 2rem 0;
  line-height: 1.6;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0 0 1.5rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 0 0 1rem 0;
  }
`;

const PurchaseDetails = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin: 2rem auto;
  text-align: left;
  max-width: 500px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1.5rem auto;
    max-width: 450px;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin: 1rem auto;
    max-width: 100%;
    border-left: none;
    border-right: none;
  }
`;

const DetailsTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 0 0.75rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin: 0 0 0.5rem 0;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  
  @media (max-width: 480px) {
    padding: 0.5rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const ProductName = styled.span`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ProductPrice = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 2px solid var(--primary-color);
  margin-top: 1rem;
  
  @media (max-width: 480px) {
    padding: 0.75rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1.125rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const TotalAmount = styled.span`
  font-weight: 700;
  color: var(--text-primary);
  font-size: 1.25rem;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0 0.5rem 0;
  border-top: 1px solid var(--border-color);
  margin-top: 0.75rem;
  
  @media (max-width: 480px) {
    padding: 0.5rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const SubtotalLabel = styled.span`
  font-weight: 500;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

const SubtotalAmount = styled.span`
  font-weight: 600;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
    align-items: center;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid var(--border-color);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--primary-color);
  color: var(--text-inverse);
  border-color: var(--primary-color);
  
  &:hover {
    background: var(--primary-dark);
    border-color: var(--primary-dark);
  }
`;

const SecondaryButton = styled(Button)`
  background: var(--background-primary);
  color: var(--text-primary);
  border-color: var(--border-color);
  
  &:hover {
    background: var(--background-secondary);
  }
`;

const ReceiptButton = styled(Button)`
  background: var(--contrast-accent);
  color: var(--text-inverse);
  border-color: var(--contrast-accent);
  
  &:hover {
    background: var(--contrast-accent-dark);
    border-color: var(--contrast-accent-dark);
  }
`;

const EmailNotice = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--primary-color);
  border-radius: 0;
  padding: 1rem;
  margin: 1.5rem auto;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  font-family: 'Inter', sans-serif;
  max-width: 500px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin: 1rem auto;
    font-size: 0.8rem;
    max-width: 450px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    margin: 0.75rem auto;
    font-size: 0.75rem;
    max-width: 100%;
    border-left: none;
    border-right: none;
  }
`;


function PurchaseConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchaseData, setPurchaseData] = useState(location.state?.purchaseData);
  const [loading, setLoading] = useState(!purchaseData);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false); // Use ref to prevent duplicate fetches across re-renders
  
  // Fetch purchase data from backend if not in location.state (Stripe redirect)
  // CRITICAL: When Stripe redirects (3D Secure, etc.), the frontend confirmation is skipped
  // The /confirmation endpoint now creates enrollments automatically if missing
  useEffect(() => {
    // Only fetch once and only if we don't have purchaseData
    if (!purchaseData && !hasFetchedRef.current) {
      const paymentIntentId = searchParams.get('payment_intent');
      
      if (paymentIntentId) {
        hasFetchedRef.current = true; // Mark as fetched to prevent duplicate calls
        setLoading(true);
        setError(null);
        
        // When Stripe redirects (3D Secure, etc.), frontend confirmation is skipped
        // The backend /confirmation endpoint now creates enrollments if missing
        // Check redirect_status first - if failed, show error immediately
        const redirectStatus = searchParams.get('redirect_status');
        if (redirectStatus === 'failed') {
          setError('Payment was declined. Please try a different payment method or contact your bank.');
          setLoading(false);
          return;
        }
        
        api.getPurchaseConfirmation(paymentIntentId)
          .then((data) => {
            // Backend validates payment status - if failed, it will return an error
            if (data.error) {
              setError(data.message || 'Payment was not successful. Please try again.');
              setLoading(false);
            } else {
              setPurchaseData(data);
              setLoading(false);
            }
          })
          .catch((err) => {
            console.error('❌ Failed to fetch purchase confirmation:', err);
            // Check if it's a payment failure error
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load purchase confirmation';
            const paymentStatus = err.response?.data?.paymentStatus;
            
            if (paymentStatus === 'failed' || errorMessage.includes('declined') || errorMessage.includes('failed')) {
              setError('Payment was declined. Please try a different payment method or contact your bank.');
            } else {
              setError(errorMessage);
            }
            setLoading(false);
            hasFetchedRef.current = false; // Allow retry on error
          });
      } else {
        setLoading(false);
      }
    }
  }, [purchaseData, searchParams]); // Include searchParams to satisfy exhaustive-deps, but hasFetchedRef prevents duplicate calls
  
  if (loading) {
    return (
      <Container>
        <ConfirmationCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Subtitle>Loading purchase confirmation...</Subtitle>
        </ConfirmationCard>
      </Container>
    );
  }
  
  if (error || !purchaseData) {
    console.error('❌ PurchaseConfirmation: No purchase data available', {
      locationState: location.state,
      pathname: location.pathname,
      search: location.search,
      error
    });
    return (
      <Container>
        <ConfirmationCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Subtitle>
            {error || 'No purchase data found. Please try again.'}
          </Subtitle>
          <ButtonGroup>
            <PrimaryButton onClick={() => navigate('/catalog')}>
              <FiShoppingBag size={16} />
              Course Catalog
            </PrimaryButton>
          </ButtonGroup>
        </ConfirmationCard>
      </Container>
    );
  }

  // Validate required fields
  if (!purchaseData.user || !purchaseData.payment) {
    console.error('❌ PurchaseConfirmation: Missing required fields in purchaseData', {
      hasUser: !!purchaseData.user,
      hasPayment: !!purchaseData.payment,
      hasCourses: !!purchaseData.courses,
      hasProducts: !!purchaseData.products,
      purchaseData
    });
    return (
      <Container>
        <ConfirmationCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Subtitle>Purchase data is incomplete. Please contact support if this issue persists.</Subtitle>
          <ButtonGroup>
            <PrimaryButton onClick={() => navigate('/catalog')}>
              <FiShoppingBag size={16} />
              Course Catalog
            </PrimaryButton>
          </ButtonGroup>
        </ConfirmationCard>
      </Container>
    );
  }

  const { user, products, courses, payment, promoCode } = purchaseData;
  
  // Validate user has required fields
  if (!user.firstName && !user.first_name) {
    console.error('❌ PurchaseConfirmation: User missing firstName', { user });
  }
  
  const totalAmount = payment.amount;
  
  // Determine if this is a course enrollment or product purchase
  const isCourseEnrollment = courses && courses.length > 0;
  const items = isCourseEnrollment ? courses : products;
  
  // Calculate the original subtotal (sum of all item prices before discount)
  const originalSubtotal = items.reduce((sum, item) => {
    const price = item.price ? parseFloat(item.price) : 0;
    return sum + price;
  }, 0);
  
  // Calculate discount amount if promo code was applied
  const discountAmount = originalSubtotal - parseFloat(totalAmount);
  const hasDiscount = discountAmount > 0.01; // Account for floating point precision
  
  // Calculate original price per item for products
  const getItemPrice = (item, index) => {
    if (isCourseEnrollment) {
      return item.price ? parseFloat(item.price) : 0;
    } else {
      // For products, show the original price, not the discounted amount
      return item.price ? parseFloat(item.price) : 0;
    }
  };

  const handleDownloadReceipt = () => {
    if (payment.receiptUrl) {
      window.open(payment.receiptUrl, '_blank');
    }
  };

  return (
    <Container>
      <ConfirmationCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SuccessIcon>
          <FiCheckCircle size={40} color="white" />
        </SuccessIcon>
        
        <Subtitle>
          {isCourseEnrollment 
            ? totalAmount === 0 
              ? `Thank you for enrolling, ${user.firstName || user.first_name || 'there'}! Your course enrollment has been successfully completed.`
              : `Thank you for enrolling, ${user.firstName || user.first_name || 'there'}! Your course enrollment has been successfully processed.`
            : `Thank you for your purchase, ${user.firstName || user.first_name || 'there'}! Your payment has been successfully processed.`
          }
        </Subtitle>

        <EmailNotice>
          A confirmation email has been sent to <strong>{user.email || 'your email address'}</strong> with your {isCourseEnrollment ? 'enrollment details' : 'purchase details and receipt'}{totalAmount === 0 && isCourseEnrollment ? ' (free enrollment)' : ''}.
        </EmailNotice>

        <ButtonGroup>
          <PrimaryButton onClick={() => navigate('/courses', { state: { refresh: true } })}>
            <FiBookOpen size={16} />
            Access My Courses
          </PrimaryButton>
          
          {payment.receiptUrl && (
            <ReceiptButton onClick={handleDownloadReceipt}>
              <FiDownload size={16} />
              Download Receipt
            </ReceiptButton>
          )}
          
          <SecondaryButton onClick={() => navigate('/catalog')}>
            <FiShoppingBag size={16} />
            Course Catalog
          </SecondaryButton>
        </ButtonGroup>

        <PurchaseDetails>
          <DetailsTitle>
            <FiShoppingBag size={20} />
            {isCourseEnrollment ? 'Enrollment Details' : 'Purchase Details'}
          </DetailsTitle>
          
          <DetailRow>
            <DetailLabel>Order Number:</DetailLabel>
            <DetailValue>#{Date.now().toString().slice(-8)}</DetailValue>
          </DetailRow>
          
          <DetailRow>
            <DetailLabel>Purchase Date:</DetailLabel>
            <DetailValue>{new Date().toLocaleDateString()}</DetailValue>
          </DetailRow>
          
          <DetailRow>
            <DetailLabel>Customer:</DetailLabel>
            <DetailValue>{(user.firstName || user.first_name || '')} {(user.lastName || user.last_name || '')}</DetailValue>
          </DetailRow>
          
          {promoCode && (
            <DetailRow>
              <DetailLabel>Promo Code Applied:</DetailLabel>
              <DetailValue style={{ color: 'var(--success-color)', fontWeight: '600' }}>
                {promoCode.code} ({promoCode.discount_type === 'percentage' 
                  ? `${promoCode.discount_value}% off` 
                  : `$${parseFloat(promoCode.discount_value).toFixed(2)} off`})
              </DetailValue>
            </DetailRow>
          )}
        </PurchaseDetails>

        <PurchaseDetails>
          <DetailsTitle>{isCourseEnrollment ? 'Courses Enrolled' : 'Items Purchased'}</DetailsTitle>
          
          {items && items.map((item, index) => (
            <ProductItem key={index}>
              <ProductName>{item.name}</ProductName>
              <ProductPrice>
                {isCourseEnrollment 
                  ? (item.price ? `$${parseFloat(item.price).toFixed(2)}` : 'Free')
                  : `$${getItemPrice(item, index).toFixed(2)}`
                }
              </ProductPrice>
            </ProductItem>
          ))}
          
          {/* Show subtotal and discount breakdown if there's a discount (either from promo code or calculated) */}
          {(hasDiscount || promoCode) && (() => {
            // If we have promo code info, use it for more accurate calculation
            let calculatedSubtotal = originalSubtotal;
            let calculatedDiscount = discountAmount;
            
            if (promoCode) {
              if (promoCode.discount_type === 'percentage') {
                // For free enrollments (totalAmount = 0), the discount equals the original subtotal
                if (parseFloat(totalAmount) === 0) {
                  calculatedSubtotal = originalSubtotal;
                  calculatedDiscount = originalSubtotal;
                } else {
                  // Reverse-calculate: finalAmount = originalAmount * (1 - discount_percentage / 100)
                  // originalAmount = finalAmount / (1 - discount_percentage / 100)
                  const discountRate = parseFloat(promoCode.discount_value) / 100;
                  calculatedSubtotal = parseFloat(totalAmount) / (1 - discountRate);
                  calculatedDiscount = calculatedSubtotal - parseFloat(totalAmount);
                }
              } else {
                // Fixed amount discount
                calculatedDiscount = parseFloat(promoCode.discount_value);
                calculatedSubtotal = parseFloat(totalAmount) + calculatedDiscount;
              }
            }
            
            return (
              <>
                <SubtotalRow>
                  <SubtotalLabel>Subtotal:</SubtotalLabel>
                  <SubtotalAmount>${calculatedSubtotal.toFixed(2)}</SubtotalAmount>
                </SubtotalRow>
                <SubtotalRow>
                  <SubtotalLabel>Discount {promoCode ? `(${promoCode.code})` : ''}:</SubtotalLabel>
                  <SubtotalAmount style={{ color: 'var(--success-color)' }}>-${calculatedDiscount.toFixed(2)}</SubtotalAmount>
                </SubtotalRow>
              </>
            );
          })()}
          
          <TotalRow>
            <TotalLabel>Total Amount:</TotalLabel>
            <TotalAmount>
              {parseFloat(totalAmount) === 0 
                ? 'Free' 
                : `$${parseFloat(totalAmount).toFixed(2)}`
              }
            </TotalAmount>
          </TotalRow>
        </PurchaseDetails>
      </ConfirmationCard>
    </Container>
  );
}

export default PurchaseConfirmation;
